// A vehicle register CSV may name a few columns differently from the register API:
// the fuel type and auxiliary fuel use by name, and files exported before the
// PRIS-3238 cutover by the old column names. The API accepts register columns only,
// so they are translated here, where the file is parsed.

// Mirrors VEHICLE_FUEL_TYPE_NAMES in lib/prism/register_rules.py.
const VEHICLE_FUEL_TYPE_NAMES = {
  DIESEL: 'Diesel',
  PETROL: 'Unleaded',
  ULP: 'Unleaded',
  UNLEADED: 'Unleaded',
  'PREMIUM UNLEADED': 'Premium unleaded',
  ETHANOL: 'E85',
  E85: 'E85',
  LPG: 'LPG',
  PROPANE: 'LPG',
  'ULP/LPG': 'Unleaded',
  LNG: 'LNG',
};

const RENAMED_COLUMNS = {
  ftc_on_road: 'ftc_on_road_eligible',
  disposed_date: 'disposed_at',
  auxiliary_fuel_percentage: 'auxiliary_fuel_percent',
};

const DROPPED_COLUMNS = ['auxiliary_safe_harbour_override', 'record_valid'];

const normaliseFuelName = (value) => value.trim().toUpperCase().replace(/\s*\/\s*/g, '/').replace(/\s+/g, ' ');

const isBlank = (value) => value === undefined || value === null || value === '';

const findIdByName = (items, name) => {
  const item = items.find((i) => i.name === name);
  return item ? item.id : null;
};

const fuelTypeIdForName = (fuelTypes, value) => {
  const name = value == null ? undefined : VEHICLE_FUEL_TYPE_NAMES[normaliseFuelName(String(value))];
  return findIdByName(fuelTypes, name);
};

// Returns a copy of the row with register columns only. A register column supplied
// alongside its alias wins, matching translate_register_file_row on the server.
export const translateRegisterFileRow = (row, { fuelTypes = [], auxFuelUses = [] } = {}) => {
  const out = { ...row };
  const setDefault = (key, value) => {
    if (!(key in out)) out[key] = value;
  };

  if ('disabled' in out) {
    const disabled = out.disabled;
    delete out.disabled;
    if (disabled != null) {
      setDefault('active', !(disabled === true || ['true', '1', 'yes'].includes(String(disabled).trim().toLowerCase())));
    }
  }
  Object.entries(RENAMED_COLUMNS).forEach(([oldName, newName]) => {
    if (oldName in out) {
      const value = out[oldName];
      delete out[oldName];
      setDefault(newName, value);
    }
  });
  if ('fuel_type' in out) {
    const value = out.fuel_type;
    delete out.fuel_type;
    setDefault('fuel_type_id', fuelTypeIdForName(fuelTypes, value));
  }
  ['auxiliary_fuel_use', 'safe_harbour_category'].forEach((oldName) => {
    if (oldName in out) {
      const value = out[oldName];
      delete out[oldName];
      setDefault('auxiliary_fuel_use_id', findIdByName(auxFuelUses, value));
    }
  });
  const method = out.auxiliary_fuel_method;
  if (typeof method === 'string' && method.trim().toUpperCase() === 'SAFE HARBOUR') {
    out.auxiliary_fuel_method = 'NONE';
    setDefault('non_public_road_calculation_method', 'SAFE_HARBOUR');
  }
  DROPPED_COLUMNS.forEach((column) => delete out[column]);
  return out;
};

// The reference lists a file's rows still need before they can be translated. Saving
// without one would write every fuel or auxiliary use the file names as null, clearing it.
export const missingReferenceLists = (rows, { fuelTypes = [], auxFuelUses = [] } = {}) => {
  const named = (keys) => rows.some((row) => keys.some((key) => !isBlank(row[key])));
  const missing = [];
  if (named(['fuel_type']) && fuelTypes.length === 0) missing.push('fuel types');
  if (named(['auxiliary_fuel_use', 'safe_harbour_category']) && auxFuelUses.length === 0) {
    missing.push('auxiliary fuel uses');
  }
  return missing;
};
